'use client'

import * as React from 'react'
import { Search, X } from 'lucide-react'
import { parseAsString, useQueryState } from 'nuqs'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useDebouncedCallback } from 'use-debounce'

export function OrdersSearch({
  className,
  search: searchQuery,
}: React.HTMLAttributes<HTMLDivElement> & {
  search?: string
}) {
  const [search, setSearch] = useQueryState(
    'search',
    parseAsString.withDefault(''),
  )
  const [inputValue, setInputValue] = React.useState(search)
  const [open, setOpen] = React.useState(false)

  // Sync input value with URL param on mount
  React.useEffect(() => {
    setInputValue(search)
  }, [search])

  // Debounced function to update URL search param
  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value || null)
  }, 500)

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    debouncedSetSearch(value)
  }

  const handleClear = () => {
    setInputValue('')
    setSearch(null)
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              'min-w-[216px] justify-start gap-2 text-left font-normal',
              !search && 'text-muted-foreground',
            )}
          >
            <Search className="size-4" />
            {search ? (
              <span className="max-w-[180px] truncate">{search}</span>
            ) : (
              <span>Search orders</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-4" align="start">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">
                Search by name, phone, or email
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-gray-500" />
                <Input
                  autoFocus
                  type="text"
                  placeholder="Enter search term..."
                  value={inputValue}
                  onChange={handleInputChange}
                  className="pl-8 pr-8"
                />
                {inputValue && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClear}
                    className="absolute right-0 top-1/2 h-8 w-8 -translate-y-1/2 p-0 hover:bg-transparent"
                  >
                    <X className="size-4 text-gray-500 hover:text-gray-700" />
                    <span className="sr-only">Clear search</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  )
}
